/** The pause card (docs/spec/04 §6): Resume, Options, Quit to title. Drawn over the stilled game. */

import { CYAN, GREY, NAVY, RED, WHITE, YELLOW } from '../../render/palette';
import type { Screen } from '../../render/screen';
import type { MenuAction, PointerInput } from '../../ui/scene';

export type PauseChoice = 'resume' | 'options' | 'quit';

const ITEMS: readonly { id: PauseChoice; label: string }[] = [
  { id: 'resume', label: 'RESUME' },
  { id: 'options', label: 'OPTIONS' },
  { id: 'quit', label: 'QUIT TO TITLE' },
];

const BOX = { x: 40, y: 104, w: 144, h: 84 };
const TOP = 138;
const ROW = 14;

export class PauseMenu {
  private sel = 0;

  reset(): void {
    this.sel = 0;
  }

  /** Move the cursor; the choice made, if this was the confirming press. */
  action(a: MenuAction): { moved: boolean; choice: PauseChoice | null } {
    if (a === 'up' || a === 'down') {
      this.sel = (this.sel + (a === 'up' ? ITEMS.length - 1 : 1)) % ITEMS.length;
      return { moved: true, choice: null };
    }
    if (a === 'confirm') return { moved: false, choice: ITEMS[this.sel]?.id ?? 'resume' };
    return { moved: false, choice: null };
  }

  /** A tap or click on an item chooses it. */
  pointer(p: PointerInput): PauseChoice | null {
    const i = Math.floor((p.y - (TOP - 3)) / ROW);
    if (p.x < BOX.x || p.x >= BOX.x + BOX.w || i < 0 || i >= ITEMS.length) return null;
    this.sel = i;
    return p.kind === 'down' && p.button === 0 ? (ITEMS[i]?.id ?? null) : null;
  }

  draw(screen: Screen): void {
    screen.faded(0.6, () => screen.fill(0, 0, screen.width, screen.height, 0));
    screen.fill(BOX.x, BOX.y, BOX.w, BOX.h, NAVY);
    screen.box(BOX.x, BOX.y, BOX.w, BOX.h, CYAN);
    screen.text('PAUSED', screen.width / 2, BOX.y + 12, RED, 'center');
    ITEMS.forEach((item, i) => {
      const on = i === this.sel;
      const y = TOP + i * ROW;
      screen.text(item.label, screen.width / 2, y, on ? YELLOW : WHITE, 'center');
      if (on) screen.text('►', BOX.x + 8, y, YELLOW);
    });
    screen.text('ESC RESUMES', screen.width / 2, BOX.y + BOX.h + 6, GREY, 'center');
  }
}
